import { useQuery } from "@tanstack/react-query";

interface InstructorData {
  _id: string;
  userId: string;
  name: string;
  email: string;
  profileImage?: string;
  instructorType?: string[];
  status: string;
  bio?: string;
  specialties?: string[];
  certifications?: string[];
  yearsOfExperience?: number;
  stats?: {
    totalStudents: number;
    totalClasses: number;
    averageRating: number;
    totalReviews: number;
  };
}

interface CheckInstructorResponse {
  instructor: InstructorData;
  isAuthenticated: boolean;
}

const checkInstructor = async (): Promise<CheckInstructorResponse> => {
  const response = await fetch("/api/auth/check-instructor", {
    credentials: "include",
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error("UNAUTHORIZED");
    }
    throw new Error("Failed to check instructor session");
  }

  const data = await response.json();
  return data;
};

export const useInstructorAuth = (enabled: boolean = true) => {
  return useQuery<CheckInstructorResponse, Error>({
    queryKey: ["instructorAuth"],
    queryFn: checkInstructor,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false, // Don't retry on 401
    enabled, // Skip the query entirely when false (e.g. non-instructor users)
  });
};

// Hook for just getting instructor data (assumes already authenticated)
export const useInstructorData = (enabled: boolean = true) => {
  const { data, isLoading, error, refetch } = useInstructorAuth(enabled);

  return {
    instructor: data?.instructor,
    isLoading: enabled ? isLoading : false,
    error,
    refetch,
    isAuthenticated: data?.isAuthenticated ?? false,
  };
};
